import React from "react";

const SubscriptionService = () => (
  <>
    <style>{`
      @keyframes slideInUp {
        from {
          opacity: 0;
          transform: translateY(30px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      @keyframes scaleIn {
        from {
          opacity: 0;
          transform: scale(0.9);
        }
        to {
          opacity: 1;
          transform: scale(1);
        }
      }

      .subscription-header {
        animation: slideInUp 0.8s ease-out;
      }

      .subscription-description {
        animation: slideInUp 0.8s ease-out 0.3s both;
      }

      .premium-card {
        animation: scaleIn 0.6s ease-out;
      }

      .premium-feature {
        animation: slideInUp 0.6s ease-out;
      }

      .premium-feature:nth-child(1) { animation-delay: 0.2s; }
      .premium-feature:nth-child(2) { animation-delay: 0.3s; }
      .premium-feature:nth-child(3) { animation-delay: 0.4s; }
      .premium-feature:nth-child(4) { animation-delay: 0.5s; }

      @keyframes bounce {
        0%, 100% {
          transform: translateY(0);
        }
        50% {
          transform: translateY(-12px);
        }
      }

      .feature-icon {
        animation: bounce 2s ease-in-out infinite;
      }

      .premium-feature:nth-child(1) .feature-icon { animation-delay: 0s; }
      .premium-feature:nth-child(2) .feature-icon { animation-delay: 0.2s; }
      .premium-feature:nth-child(3) .feature-icon { animation-delay: 0.4s; }
      .premium-feature:nth-child(4) .feature-icon { animation-delay: 0.6s; }

      .premium-card {
        transition: all 0.4s ease;
        position: relative;
        overflow: hidden;
      }

      .premium-card::before {
        content: '';
        position: absolute;
        top: -2px;
        left: -2px;
        right: -2px;
        bottom: -2px;
        background: linear-gradient(135deg, #1A7A7A, #0D9DAD, #1A7A7A);
        border-radius: 1.5rem;
        z-index: -1;
        opacity: 0;
        transition: opacity 0.4s ease;
      }

      .premium-card:hover::before {
        opacity: 1;
      }

      .premium-card:hover {
        transform: translateY(-15px) scale(1.02);
      }
    `}</style>

    <section id="subscription" className="py-24 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto mb-16 subscription-header">
          <span className="inline-flex items-center gap-2 bg-slate-700/50 border border-slate-400/40 rounded-full px-4 py-2 text-xs font-semibold text-slate-300 mb-6 backdrop-blur-sm">
            <span className="w-2 h-2 bg-slate-400 rounded-full animate-pulse" />
            Premium AI Services
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6 leading-tight">
            Specialized AI Channel Center
          </h2>
          <p className="subscription-description text-white/80 text-lg leading-relaxed">
            Unlock advanced AI-powered healthcare with our premium subscription service.<br />
            Get access to cutting-edge diagnostic tools and personalized health support.
          </p>
        </div>

        {/* Pricing Card */}
        <div className="premium-card bg-gradient-to-br from-slate-800 to-slate-700 rounded-2xl p-12 border border-teal-500/30 max-w-2xl mx-auto mb-16">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2">
              <span className="text-5xl font-bold text-white">$</span>
              <div className="text-6xl font-bold text-white">20</div>
            </div>
            <div className="text-lg text-white/70">Per Month</div>
            <div className="text-sm text-white/50 mt-2">Unlimited access to all premium features</div>
          </div>

          <button className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-4 rounded-xl font-bold text-lg hover:scale-105 transform transition-all duration-300 shadow-lg">
            Subscribe Now
          </button>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {[
            { icon: "🖼️", title: "2D/3D Image Analysis", desc: "Advanced medical image analysis" },
            { icon: "📋", title: "Smart Prescription Reading", desc: "Digital prescription processing" },
            { icon: "📄", title: "Medical Report Explanation", desc: "AI-powered interpretation of medical reports" },
            { icon: "🤖", title: "Medical AI Chatbot", desc: "24/7 AI health assistant" }
          ].map((feature, idx) => (
            <div key={idx} className="premium-feature bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-6 hover:bg-white/15 hover:border-teal-400/50 transition-all duration-300">
              <div className="text-4xl mb-4 feature-icon">{feature.icon}</div>
              <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
              <p className="text-white/70 text-sm">{feature.desc}</p>
            </div>
          ))}
        </div>

        {/* How It Works */}
        <div className="bg-gradient-to-r from-teal-600/20 to-cyan-600/20 rounded-2xl p-12 border border-teal-400/30">
          <h3 className="text-3xl font-bold text-white mb-8">How Premium AI Works</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-600 to-cyan-600 flex items-center justify-center text-2xl font-bold text-white mx-auto mb-4">1</div>
              <h4 className="text-lg font-bold text-white mb-2">Upload</h4>
              <p className="text-white/70">Upload medical images, prescriptions, or medical reports to the platform.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-600 to-blue-600 flex items-center justify-center text-2xl font-bold text-white mx-auto mb-4">2</div>
              <h4 className="text-lg font-bold text-white mb-2">AI Analysis</h4>
              <p className="text-white/70">Advanced AI analyzes and processes the data instantly</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-600 to-teal-600 flex items-center justify-center text-2xl font-bold text-white mx-auto mb-4">3</div>
              <h4 className="text-lg font-bold text-white mb-2">Results</h4>
              <p className="text-white/70">Get detailed insights and expert recommendations</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  </>
);

export default SubscriptionService;
